/**
 * ContractDetail — Phase 7 Sub-iter 7F.
 *
 * Wave 29 Chunk 5 rewritten on the EntityShell + tabs pattern.  Tabs:
 *   Body         — the contract text as a read-only render
 *   Signatures   — list + inline composer + 2-step cancel confirm
 *   Activity     — HistoryBlock audit trail
 *
 * Trust Contract Rule 2:
 *   • `window.prompt("Copy sign link…")` fallback removed — the modern
 *     clipboard API has near-universal support; on failure we surface a
 *     toast with the URL the user can long-press to copy.
 *   • `window.confirm("Cancel signature request…")` replaced with an
 *     inline pending-state + Confirm/Cancel buttons.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Plus, Send, Copy, Loader2, CheckCircle2, Clock, X, Trash2,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { EntityShell } from "../components/shell";
import HistoryBlock from "../components/shell/HistoryBlock";

export default function ContractDetail() {
  const { cid } = useParams();
  const [contract, setContract] = useState(null);
  const [sigs, setSigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reqOpen, setReqOpen] = useState(false);
  const [signerEmail, setSignerEmail] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerUserId, setSignerUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(null); // signature row
  const [tab, setTab] = useState("body");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: s }, { data: u }] = await Promise.all([
        api.get(`/contracts/${cid}`),
        api.get(`/contracts/${cid}/signatures`),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setContract(c);
      setSigs(s.items || []);
      setUsers(Array.isArray(u) ? u : (u?.items || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load contract.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const requestSig = async () => {
    if (!signerUserId && !signerEmail && !signerName) {
      return toast.error("Pick a teammate or enter an email / name.");
    }
    setSaving(true);
    try {
      await api.post(`/contracts/${cid}/signatures`, {
        user_id: signerUserId || null,
        email: signerEmail.trim() || null,
        name: signerName.trim() || null,
      });
      toast.success("Signature requested. Copy the link to share.");
      setReqOpen(false); setSignerUserId(""); setSignerEmail(""); setSignerName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed.");
    } finally { setSaving(false); }
  };

  const copyLink = async (s) => {
    const url = `${window.location.origin}/sign/${s.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Sign link copied.");
    } catch {
      // Trust Contract Rule 2 — no window.prompt.  Surface the URL via
      // a long-lived toast so the user can long-press / select-copy it.
      toast.error(`Copy failed — ${url}`, { duration: 12000 });
    }
  };

  const confirmCancel = async () => {
    if (!pendingCancel) return;
    try {
      await api.delete(`/contracts/${cid}/signatures/${pendingCancel.id}`);
      toast.success("Cancelled.");
      setPendingCancel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed.");
    }
  };

  if (loading) return <div className="h-40 hv-card animate-pulse" />;
  if (!contract) return null;

  const pendingCount = sigs.filter((s) => !s.signed_at).length;
  const signedCount = sigs.filter((s) => s.signed_at).length;
  const statusTone = pendingCount === 0 && signedCount > 0 ? "green"
    : pendingCount > 0 ? "amber"
    : "slate";
  const statusLabel = pendingCount === 0 && signedCount > 0 ? "fully signed"
    : pendingCount > 0 ? `${pendingCount} pending`
    : "draft";

  const headerActions = (
    <button
      onClick={() => { setReqOpen(true); setTab("signatures"); }}
      className="hv-btn hv-btn-primary text-xs"
      data-testid="contract-request-sig-btn"
    >
      <Plus className="w-3.5 h-3.5" /> Request signature
    </button>
  );

  const tabs = [
    { key: "body", label: "Body" },
    { key: "signatures", label: "Signatures", count: sigs.length || undefined },
    { key: "activity", label: "Activity" },
  ];

  return (
    <EntityShell
      kindLabel={`Contract · ${contract.kind || ""}`.trim()}
      title={contract.title}
      status={{ label: statusLabel, tone: statusTone }}
      backTo="/contracts"
      actions={headerActions}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
    >
      <div className="space-y-4" data-testid="contract-detail-page">
        {pendingCancel && (
          <div
            className="hv-card border-rose-500/40 bg-rose-500/5 p-3 flex items-center justify-between gap-2"
            data-testid="contract-cancel-confirm"
          >
            <span className="text-xs text-rose-200">
              Cancel signature request for <strong>{pendingCancel.signer_name || pendingCancel.signer_email}</strong>?
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPendingCancel(null)}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="contract-cancel-back"
              >
                Keep
              </button>
              <button
                type="button"
                onClick={confirmCancel}
                className="hv-btn hv-btn-primary text-xs bg-rose-500 hover:bg-rose-400"
                data-testid="contract-cancel-confirm-btn"
              >
                <Trash2 className="w-3 h-3" /> Confirm cancel
              </button>
            </div>
          </div>
        )}

        {tab === "body" && (
          <div className="hv-card p-4" data-testid="contract-body-tab">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">{contract.kind}</div>
            <pre className="text-sm text-[#cfd6e3] whitespace-pre-wrap leading-relaxed font-sans" data-testid="contract-body-render">{contract.body}</pre>
          </div>
        )}

        {tab === "signatures" && (
          <div className="space-y-3" data-testid="contract-signatures-tab">
            {reqOpen && (
              <div className="hv-card p-4 space-y-3" data-testid="sig-request-composer">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Request signature</h3>
                  <button onClick={() => setReqOpen(false)} className="text-[#647087]"><X className="w-4 h-4" /></button>
                </div>
                <select value={signerUserId} onChange={(e) => setSignerUserId(e.target.value)} className="hv-input text-sm w-full" data-testid="sig-user-select">
                  <option value="">— pick a teammate (or fill out email/name below) —</option>
                  {users.filter((u) => u.role !== "client").map((u) => (
                    <option key={u.id} value={u.id}>{u.name || u.email} · {u.role}</option>
                  ))}
                </select>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} placeholder="Signer email (external)" className="hv-input text-sm" />
                  <input type="text" value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Signer name (external)" className="hv-input text-sm" />
                </div>
                <button onClick={requestSig} disabled={saving} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="sig-request-save">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
                </button>
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-2">Signatures ({sigs.length})</div>
              {sigs.length === 0 ? (
                <div className="text-xs text-[#647087]">No signature requests yet.</div>
              ) : (
                <div className="space-y-2" data-testid="signatures-list">
                  {sigs.map((s) => (
                    <div key={s.id} className="hv-card p-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`signature-${s.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white">{s.signed_full_name || s.signer_name || s.signer_email}</div>
                        <div className="text-[10px] text-[#647087]">
                          {s.signed_at ? (
                            <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 className="w-3 h-3" /> signed {new Date(s.signed_at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-300"><Clock className="w-3 h-3" /> pending · expires {new Date(s.expires_at).toLocaleDateString("en-ZA")}</span>
                          )}
                        </div>
                        {s.audit_hash && (
                          <div className="text-[9px] text-[#3a4357] font-mono truncate mt-0.5" title={s.audit_hash}>SHA-256: {s.audit_hash.slice(0, 16)}…</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {!s.signed_at && (
                          <>
                            <button onClick={() => copyLink(s)} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`sig-copy-${s.id}`}>
                              <Copy className="w-3 h-3" /> Copy link
                            </button>
                            <button onClick={() => setPendingCancel(s)} className="hv-btn hv-btn-ghost text-[10px] text-red-300" data-testid={`sig-cancel-${s.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "activity" && (
          <HistoryBlock entity={{ id: cid, kind: "contract" }} />
        )}
      </div>
    </EntityShell>
  );
}
